import React, { useMemo } from "react";
import {
  Box,
  Text,
  Stack,
  Step,
  StepIcon,
  StepIndicator,
  StepSeparator,
  StepStatus,
  StepTitle,
  StepDescription,
  Stepper,
  Avatar,
} from "@chakra-ui/react";
import { FaCircleCheck } from "react-icons/fa6";
import { servicebookingFlowStore } from "../store/ServiceBookingFlowStore";
import { patientStore } from "../store/PatientStore";
import { observer } from "mobx-react-lite"; // Import the observer
import { formatDate } from "../lib/util"; // Import the formatDate function from util.ts

// Define the expected interface for props
interface BookingSidebarProps {
  activeStep: number;
  user: any;
  isLoggedIn: boolean;
  patient: any;
  selectedDoctor: any;
  selectedTime: any;
  goToLogin: () => void;
  showExistingPatientStep?: boolean; // New prop to control step visibility
}

// Wrap the component with observer HOC
const BookingSidebar: React.FC<BookingSidebarProps> = observer(
  ({
    activeStep,
    user,
    isLoggedIn,
    patient,
    selectedDoctor,
    selectedTime,
    goToLogin,
    showExistingPatientStep = true, // Default to showing the step if not specified
  }) => {
    // Create the steps array dynamically based on showExistingPatientStep
    const STEPS = useMemo(() => {
      //const baseSteps = [{ label: "User Selection" }];
      const baseSteps = [{ label: "Date and Time Selection" }];

      // Only include the Existing Patient step if the flag is true
      if (showExistingPatientStep) {
        baseSteps.push({ label: "Existing Patient" });
      }

      // Add the remaining steps
      baseSteps.push(
        { label: "Enter your details" },
        { label: "Confirmation" }
      );

      return baseSteps;
    }, [showExistingPatientStep]);

    const getStepDescription = (index) => {
      const { appointmentData } = servicebookingFlowStore;
      const actualStepIndex = showExistingPatientStep
        ? index
        : adjustStepIndex(index);

      switch (actualStepIndex) {
        case 0: // User Selection
          // return appointmentData.isForUser === null
          //   ? "Not selected"
          //   : appointmentData.isForUser
          //     ? "User"
          //     : "Family Member";
          return appointmentData.selectedTime ? formatDate(selectedTime?.date).replace(timeRegex, "").trim() + " " + appointmentData.selectedTime?.formattedTime : "Not selected";
        case 1: // Existing Patient (may be skipped)
          if (!showExistingPatientStep) {
            // This case won't be reached if Existing Patient is hidden
            return "";
          }
          if (patient?.is_new_pt === undefined)
            return appointmentData.isExistingPatient ? "Yes" : "No";
          return patient?.is_new_pt === "0" ? "Yes" : "No";
        case 2: // Time Selection (may be index 1 if Existing Patient is hidden)
          // return appointmentData.selectedTime?.formattedTime || "Not selected";
          return patientStore.currentPatient
            ? `${patientStore.currentPatient.first_name} ${patientStore.currentPatient.last_name}`
            : "Not logged in";
        
        default:
          return "";
      }
    };

    // Helper function to adjust step index if Existing Patient step is hidden
    const adjustStepIndex = (visibleIndex) => {
      // If we're showing all steps, no adjustment needed
      if (showExistingPatientStep) {
        return visibleIndex;
      }

      // If Existing Patient step is hidden, and we're past the User Selection step,
      // we need to add 1 to get the correct store index
      if (visibleIndex >= 1) {
        return visibleIndex + 1;
      }

      return visibleIndex;
    };

    // Adjust active step for display based on the visibility of Existing Patient step
    const getDisplayActiveStep = () => {
      if (showExistingPatientStep) {
        return activeStep;
      }

      // If Existing Patient step is hidden and we're past it,
      // we need to subtract 1 from the active step
      if (activeStep > 1) {
        return activeStep - 1;
      }

      return activeStep;
    };

    // Get the current patient from the store - now this will be reactive
    const currentPatient = patientStore.currentPatient;
    const displayActiveStep = getDisplayActiveStep();
    const timeRegex = /^(\d{1,2}\.\d{2}\s*(?:am|pm))/i;
    return (
      <Box
        bg="gray.100"
        width="15%"
        minWidth="300px"
        minHeight="85vh"
        p={6}
        borderRadius="md"
        boxShadow="sm"
        display={{ base: "none", lg: "flex" }}
        flexDirection="column"
      >
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {currentPatient?.first_name ? (
            <Text fontWeight="bold">Hi, {currentPatient?.first_name}</Text>
          ) : (
            <>
              {/*<Text mr={2}>Have an account?</Text> 
              <Button
                variant="outline"
                color={
                  servicebookingFlowStore?.configurationDetails?.colorScheme
                    ?.buttonBg || "#B92F2F"
                }
                size="sm"
                border="2px"
                onClick={goToLogin}
                _hover={{
                  bg:
                    servicebookingFlowStore?.configurationDetails?.colorScheme
                      ?.buttonBg || "#B92F2F",
                  color: "#fff",
                }}
              >
                Login
              </Button>
              */}
            </>
            
          )}
        </Box>

        <Stack mt={5}>
          <Stepper
            size="sm"
            index={displayActiveStep}
            orientation="vertical"
            height="200px"
            gap="0"
          >
            {STEPS.map((step, index) => {
              const stepDescription = getStepDescription(index);
              const isCompletedStep =
                !!stepDescription &&
                stepDescription !== "Not selected" &&
                stepDescription !== "Not logged in" &&
                stepDescription !== "No";

              return (
                <Step key={index}>
                  <StepIndicator>
                    <StepStatus
                      complete={<StepIcon />}
                      incomplete={
                        isCompletedStep ? (
                          <FaCircleCheck size="md" color="#3182ce" />
                        ) : (
                          null
                        )
                      }
                    />
                  </StepIndicator>

                  <Box flexShrink="0">
                    <StepTitle>{step.label}</StepTitle>
                    <StepDescription>{stepDescription}</StepDescription>
                  </Box>
                  <StepSeparator />
                </Step>
              );
            })}
          </Stepper>
        </Stack>

        {selectedDoctor && (
          <Box display="flex" flexDirection="row" alignItems="center" mt={5}>
            <Avatar
              name={selectedDoctor.display_name}
              size="md"
              src={selectedDoctor.image}
            />
            <Box display="flex" flexDirection="column" ml={3}>
              <Text fontWeight="bold">{selectedDoctor.display_name}</Text>
              {selectedTime?.formattedTime && (
                <Text fontSize="sm" color="gray.600">
                  {selectedTime.formattedTime}{" "}
                  {formatDate(selectedTime?.date).replace(timeRegex, "").trim()}
                </Text>
              )}
            </Box>
          </Box>
        )}

        {currentPatient?.first_name && (
          <Box bg="white" p={3} mt={4} borderRadius="md" boxShadow="sm">
            <Text fontWeight="bold">Patient Details</Text>
            <Text fontSize="sm">
              <b>Name:</b> {currentPatient?.first_name || "N/A"}{" "}
              {currentPatient?.last_name || "N/A"}
            </Text>
            <Text fontSize="sm">
              <b>Date of Birth:</b> {currentPatient?.dob || "N/A"}
            </Text>
            {currentPatient?.contact_number && (
              <Text fontSize="sm">
                <b>Contact:</b> {currentPatient.contact_number}
              </Text>
            )}
          </Box>
        )}

        <Box flexGrow={1} mt={5} />
      </Box>
    );
  }
);

export default BookingSidebar;
